import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from "primeng/api";
import {TicketGroupFilterService} from "../../../../../service/api/ticket-group-filter.service";
import {TicketGroupFilterDto} from "../../../../../model/dto/entity/ticket-group-filter.dto";
import {TicketGroupFilterAccess} from "../../../../../enum/ticket-group-filter-access.enum";
import {ObjectBaseComponent} from "../../../../../../../core/shared/object/mvs-object-base/object-base.component";
import {MvsCoreService} from "../../../../../../../core/shared/service/mvs-core.service";
import {MvsMessageService} from "../../../../../../../core/shared/service/message/mvs-message.service";
import {ObserverService} from "../../../../../../../core/shared/object/service/observer.service";
import {MvsObjectNavigationService} from "../../../../../../../core/shared/navigation/mvs-object-navigation.service";
// TM-1000001563: replace <mvs-object> with <mvs-widget> (uiComponent='object')
// to skip the create-mode GET /ticketGroupFilters/0 (404).
import {WidgetData} from "../../../../../../../core/shared/widget/widget-data";
import {WidgetFactory} from "../../../../../../../core/shared/widget/service/widget.factory";
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from "../../../../../../../core/shared/logic/object-change-information";

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Verwaltet die Liste der Ticketgruppenfilter und bettet ein <mvs-widget> mit uiComponent='object' für Anlage und Bearbeitung von tm.TicketGroupFilter ein, um den 404-Aufruf GET /ticketGroupFilters/0 im Anlagemodus zu vermeiden.
 *
 * @ticket TM-1000001563
 */
@Component({
    selector: 'ticket-filter-group-list',
    templateUrl: './ticket-filter-group-list.component.html',
    standalone: false
})
export class TicketFilterGroupListComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    groupFilters: TicketGroupFilterDto[];
    selectedGroup: TicketGroupFilterDto;
    currentView: any | 'allFilters' | 'createFilter' | 'updateFilter' = 'allFilters';

    // TM-1000001563: widget configurations for inline create/update of tm.TicketGroupFilter via <mvs-widget>.
    createFilterWidget: WidgetData;
    updateFilterWidget: WidgetData;

    constructor(protected coreService: MvsCoreService,
                protected messageService: MvsMessageService,
                protected confirmationService: ConfirmationService,
                protected observerService: ObserverService,
                protected navigationService: MvsObjectNavigationService,
                protected ticketGroupFilterService: TicketGroupFilterService
                ) {
        super(coreService,messageService,confirmationService,observerService);
    }
    protected retrieveObject() {
        this.onObjectChanged();
    }

    /**
     * On Object Change.
     */
    onObjectChanged() {
        this.getTicketGroupFilters();
    }

    getTicketGroupFilters() {
        this.ticketGroupFilterService.getMeGroups().subscribe(res => {
            this.groupFilters = res.entries;
            this.initialized = true;
        });
    }

    handleEditFilter(group: TicketGroupFilterDto) {
        // TM-1000001563: build the update widget with the selected filter's id before switching view.
        this.selectedGroup = group;
        this.updateFilterWidget = this.buildTicketGroupFilterWidget('tm.ticketGroupFilter.update', group.id);
        this.currentView = "updateFilter";
    }

    handleCreateFilter() {
        // TM-1000001563: build the create widget before switching view so <mvs-widget> renders with valid data.
        this.createFilterWidget = this.buildTicketGroupFilterWidget('tm.ticketGroupFilter.create', 0);
        this.currentView = 'createFilter';
    }

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: Erzeugt ein WidgetData für MvsWidgetObjectComponent zur Anlage bzw. Bearbeitung von tm.TicketGroupFilter; objectId=0 aktiviert den Anlagemodus und verhindert den 404-Aufruf GET /ticketGroupFilters/0.
     *
     * Build a WidgetData targeting MvsWidgetObjectComponent for tm.TicketGroupFilter.
     * objectId = 0 -> create mode (widget skips the GET and uses createDefaultDto).
     * objectId > 0 -> update mode (widget loads the entity via mvs-crud-object).
     *
     * @ticket TM-1000001563
     */
    private buildTicketGroupFilterWidget(idAlias: string, objectId: number): WidgetData {
        // TM-1000001563: WidgetFactory.createWidgetObject yields uiComponent='object', dataProvider='list', dataSource='entity'.
        return WidgetFactory.createWidgetObject(
            idAlias,
            'Filter',
            'tm.TicketGroupFilter',
            objectId
        );
    }

    /**
     * Handle object-changed event from the embedded create/update widget.
     * Refreshes the filter list and returns to the overview only on
     * created/updated/deleted actions; no-op for unknown actions.
     *
     * @ticket TM-1000001563
     */
    handleFilterChanged(event: ObjectChangeInformation) {
        // TM-1000001563: only react to lifecycle changes that actually mutated the entity.
        if (!event) {
            return;
        }
        if (event.action === ObjectChangeInformationActionEnum.created
            || event.action === ObjectChangeInformationActionEnum.updated
            || event.action === ObjectChangeInformationActionEnum.deleted) {
            this.handleMainNavigation();
        }
    }

    handleMainNavigation() {
        this.getTicketGroupFilters();
        this.currentView = 'allFilters';
    }

    /**
     * Handle delete filter with confirmation dialog.
     *
     * @ticket TM-1000001457
     */
    handleDeleteFilter(group: TicketGroupFilterDto) {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the filter "${group.name}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.ticketGroupFilterService.delete(group.id).subscribe({
                    next: () => {
                        this.messageService.showSuccessMessage('Filter Deleted', `Filter "${group.name}" has been deleted successfully.`);
                        this.getTicketGroupFilters();
                    },
                    error: (err) => {
                        this.messageService.showErrorMessage('Delete Failed', 'Failed to delete the filter. Please try again.');
                    }
                });
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    protected readonly TicketGroupFilterAccess = TicketGroupFilterAccess;
}
